import { DateTime } from 'luxon';
import {
    AddConcertImageDto,
    AddLocationImageDto,
    Banner2,
    CatalogMediaControllerClient,
    CommentDto,
    ConcertCatalogControllerClient,
    IConcertAdminDto,
    IConcertSeriesAdminDto,
    ILocationAdminDto,
    IPlanAdminDto,
    IStoredFileDto,
    ImageDto,
    LinkDto,
    Order,
    Picture2,
    PlanCatalogControllerClient,
    PlanAdminDto,
    PlanDto,
    ReuseConcertPlanDto,
    RemoveConcertImageDto,
    RemoveLocationImageDto,
    SortBy,
    UpdateConcertPlanDto,
    UpdateConcertPlanDtoMode,
    UpsertConcertDto,
    UpsertConcertSeriesDto,
    UpsertLocationDto,
    UpsertPlanDto,
    UpdateConcertSeriesBannerDto,
    UpdateConcertSeriesPictureDto,
} from '../clients/services';
import clientFactory from './client-factory';
import { FileLibraryService } from './file-library.service';

const EMPTY_IMAGE_URL = '';

export class ConcertSeriesWorkbenchService {
    private readonly catalogClient: ConcertCatalogControllerClient;
    private readonly planClient: PlanCatalogControllerClient;
    private readonly mediaClient: CatalogMediaControllerClient;
    private readonly fileLibraryService: FileLibraryService;

    constructor() {
        this.catalogClient = clientFactory.createSecuredClient(ConcertCatalogControllerClient);
        this.planClient = clientFactory.createSecuredClient(PlanCatalogControllerClient);
        this.mediaClient = clientFactory.createSecuredClient(CatalogMediaControllerClient);
        this.fileLibraryService = new FileLibraryService();
    }

    async loadSeriesAsync(): Promise<IConcertSeriesAdminDto[]> {
        const series = await this.catalogClient.getConcertSeries();
        return series.sort(
            (a: IConcertSeriesAdminDto, b: IConcertSeriesAdminDto) =>
                this.getDateScore(b.seriesStart) - this.getDateScore(a.seriesStart),
        );
    }

    async loadConcertsAsync(): Promise<IConcertAdminDto[]> {
        const concerts = await this.catalogClient.getConcerts();
        return concerts.sort(
            (a: IConcertAdminDto, b: IConcertAdminDto) => this.getDateScore(a.date) - this.getDateScore(b.date),
        );
    }

    async loadLocationsAsync(): Promise<ILocationAdminDto[]> {
        const locations = await this.catalogClient.getLocations();
        return locations.sort((a: ILocationAdminDto, b: ILocationAdminDto) =>
            (a.name || '').localeCompare(b.name || '', 'de-CH'),
        );
    }

    async loadPlansAsync(): Promise<IPlanAdminDto[]> {
        const plans = await this.planClient.getPlans();
        return plans.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de-CH'));
    }

    async createLocationAsync(name: string): Promise<ILocationAdminDto> {
        const payload = new UpsertLocationDto({
            name: name.trim(),
            description: null,
            comment: null,
            street: null,
            zip: null,
            city: null,
            country: null,
            phone: null,
            links: [],
        });

        return await this.catalogClient.createLocation(payload);
    }

    async updateLocationAsync(locationId: number, location: ILocationAdminDto): Promise<ILocationAdminDto> {
        const payload = new UpsertLocationDto({
            name: (location.name || '').trim(),
            description: this.toNullableString(location.description),
            comment: this.toNullableString(location.comment),
            street: this.toNullableString(location.street),
            zip: this.toNullableString(location.zip),
            city: this.toNullableString(location.city),
            country: this.toNullableString(location.country),
            phone: this.toNullableString(location.phone),
            links: (location.links ?? []).map(
                (link) =>
                    new LinkDto({
                        title: this.toNullableString(link.title) ?? '',
                        url: this.toNullableString(link.url) ?? '',
                    }),
            ),
        });

        return await this.catalogClient.updateLocation(locationId, payload);
    }

    async deleteLocationAsync(locationId: number): Promise<void> {
        await this.catalogClient.deleteLocation(locationId);
    }

    async addLocationImageAsync(locationId: number, image: ImageDto): Promise<ILocationAdminDto> {
        return await this.mediaClient.addLocationImage(locationId, new AddLocationImageDto({ image }));
    }

    async removeLocationImageAsync(locationId: number, url: string): Promise<ILocationAdminDto> {
        return await this.mediaClient.removeLocationImage(locationId, new RemoveLocationImageDto({ url }));
    }

    async createPlanAsync(name: string, locationId?: number | null): Promise<IPlanAdminDto> {
        const trimmedName = name.trim();
        const plan = new PlanDto({
            id: 1,
            name: trimmedName,
            blocks: [],
        });

        const payload = new UpsertPlanDto({
            name: trimmedName,
            locationId: locationId ?? null,
            plan,
        });

        return await this.planClient.createPlan(payload);
    }

    async deletePlanAsync(planId: number): Promise<void> {
        await this.planClient.deletePlan(planId);
    }

    async createSeriesAsync(title: string): Promise<IConcertSeriesAdminDto> {
        const payload = new UpsertConcertSeriesDto({
            title,
            hide: true,
            hideConcerts: false,
            comments: [],
            links: [],
        });
        return await this.catalogClient.createConcertSeries(payload);
    }

    async updateSeriesAsync(seriesId: number, update: UpsertConcertSeriesDto): Promise<IConcertSeriesAdminDto> {
        return await this.catalogClient.updateConcertSeries(seriesId, update);
    }

    async publishSeriesAsync(series: IConcertSeriesAdminDto): Promise<IConcertSeriesAdminDto> {
        const payload = this.toSeriesUpsert(series);
        payload.hide = false;
        return await this.catalogClient.updateConcertSeries(series.id, payload);
    }

    async createConcertAsync(
        seriesId: number,
        title: string,
        ticketSaleStart?: DateTime | null,
    ): Promise<IConcertAdminDto> {
        const payload = new UpsertConcertDto({
            concertSerieId: seriesId,
            title,
            ticketSaleStart: ticketSaleStart ?? null,
            hide: false,
            ticketSale: true,
            soldOut: false,
            ticketSaleBlocked: false,
            concertCancelled: false,
            comments: [],
            artistIds: [],
        });
        return await this.catalogClient.createConcert(payload);
    }

    async updateConcertAsync(concertId: number, payload: UpsertConcertDto): Promise<IConcertAdminDto> {
        return await this.catalogClient.updateConcert(concertId, payload);
    }

    async deleteConcertAsync(concertId: number): Promise<void> {
        await this.catalogClient.deleteConcert(concertId);
    }

    async setSeriesBannerAsync(seriesId: number, image?: ImageDto | null): Promise<IConcertSeriesAdminDto> {
        if (!image || !image.url || image.url === EMPTY_IMAGE_URL) {
            return await this.mediaClient.removeConcertSeriesBanner(seriesId);
        }

        return await this.mediaClient.addConcertSeriesBanner(
            seriesId,
            new UpdateConcertSeriesBannerDto({ banner: new Banner2(image) }),
        );
    }

    async setSeriesPictureAsync(seriesId: number, image?: ImageDto | null): Promise<IConcertSeriesAdminDto> {
        if (!image || !image.url || image.url === EMPTY_IMAGE_URL) {
            return await this.mediaClient.removeConcertSeriesPicture(seriesId);
        }

        return await this.mediaClient.addConcertSeriesPicture(
            seriesId,
            new UpdateConcertSeriesPictureDto({ picture: new Picture2(image) }),
        );
    }

    async addConcertImageAsync(concertId: number, image: ImageDto): Promise<IConcertAdminDto> {
        return await this.mediaClient.addConcertImage(concertId, new AddConcertImageDto({ image }));
    }

    async removeConcertImageAsync(concertId: number, url: string): Promise<IConcertAdminDto> {
        return await this.mediaClient.removeConcertImage(concertId, new RemoveConcertImageDto({ url }));
    }

    async updateConcertPlanModeAsync(
        concertId: number,
        mode: UpdateConcertPlanDtoMode,
        planId?: number | null,
    ): Promise<IConcertAdminDto> {
        return await this.planClient.updateConcertPlan(
            concertId,
            new UpdateConcertPlanDto({
                mode,
                planId: planId ?? null,
            }),
        );
    }

    async saveConcertPlanAsTemplateAsync(
        concertId: number,
        name: string,
        locationId?: number | null,
    ): Promise<IPlanAdminDto> {
        const created: PlanAdminDto = await this.planClient.reuseConcertPlan(
            concertId,
            new ReuseConcertPlanDto({
                name,
                locationId: locationId ?? null,
            }),
        );

        return created;
    }

    async listMediaImagesAsync(): Promise<IStoredFileDto[]> {
        return await this.fileLibraryService.listFilesAsync('', SortBy.CreatedAt, Order.Desc, '');
    }

    async uploadMediaImageAsync(file: File): Promise<IStoredFileDto> {
        return await this.fileLibraryService.uploadFileAsync(file, file.name, '', 'images');
    }

    toSeriesUpsert(series: IConcertSeriesAdminDto): UpsertConcertSeriesDto {
        return new UpsertConcertSeriesDto({
            title: series.title,
            artistIds: series.artistIds ?? [],
            comments: (series.comments ?? []).map((c) => new CommentDto(c)),
            content: series.content,
            links: series.links ?? [],
            ticketSaleStart: series.ticketSaleStart ?? null,
            seriesStart: series.seriesStart ?? null,
            seriesEnd: series.seriesEnd ?? null,
            hide: series.hide ?? false,
            hideConcerts: series.hideConcerts ?? false,
        });
    }

    toConcertUpsert(concert: IConcertAdminDto): UpsertConcertDto {
        return new UpsertConcertDto({
            concertSerieId: concert.concertSerieId ?? null,
            date: concert.date ?? null,
            doors: this.toNullableString(concert.doors),
            start: this.toNullableString(concert.start),
            title: concert.title,
            artistTitle: this.toNullableString(concert.artistTitle),
            planId: concert.planId ?? null,
            locationId: concert.locationId ?? null,
            saleOptions: concert.saleOptions,
            restrictionOptions: concert.restrictionOptions,
            comments: concert.comments ?? [],
            artistIds: concert.artistIds ?? [],
            ticketSaleStart: concert.ticketSaleStart ?? null,
            ticketSaleEnd: concert.ticketSaleEnd ?? null,
            soldOut: concert.soldOut ?? false,
            ticketSale: concert.ticketSale ?? true,
            ticketSaleBlocked: concert.ticketSaleBlocked ?? false,
            concertCancelled: concert.concertCancelled ?? false,
            price: concert.price ?? null,
            hide: concert.hide ?? false,
        });
    }

    private toNullableString(value?: string | null): string | null {
        if (!value) {
            return null;
        }

        const normalized = value.trim();
        return normalized.length > 0 ? normalized : null;
    }

    private getDateScore(value?: DateTime | null): number {
        return value ? value.toMillis() : 0;
    }
}
